import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Snackbar,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import Logo from "../assests/images/component/logo.png";
import Twitter from "../assests/images/component/twitter.svg";
import Instagram from "../assests/images/component/instagram.svg";
import LinkedIn from "../assests/images/component/linkedIn.svg";
import Youtube from "../assests/images/component/youtube.svg";
import Whatsapp from "../assests/images/component/whatsapp.svg";
import Behance from "../assests/images/component/behance.svg";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

const NAV_LINKS: { label: string; route: string }[] = [
  { label: "Home", route: "/" },
  { label: "Services", route: "/services" },
  { label: "About us", route: "/about" },
  { label: "Portfolio", route: "/portfolio" },
  { label: "Careers", route: "/career" },
];

const SERVICE_LINKS: { label: string; route: string }[] = [
  { label: "Web development", route: "/webdeveloper" },
  { label: "Mobile apps", route: "/mobileapp" },
  { label: "E-commerce", route: "/ecommerce" },
  { label: "UI/UX design", route: "/ui" },
  { label: "DevOps", route: "/devops" },
  { label: "CMS", route: "/cms" },
  { label: "HMS", route: "/hms" },
];

const SOCIAL_LINKS: { icon: string; name: string; href: string }[] = [
  { icon: Twitter, name: "Twitter", href: "#" },
  { icon: Instagram, name: "Instagram", href: "#" },
  { icon: LinkedIn, name: "LinkedIn", href: "#" },
  { icon: Youtube, name: "YouTube", href: "#" },
  { icon: Whatsapp, name: "WhatsApp", href: "#" },
  { icon: Behance, name: "Behance", href: "#" },
];

const ColumnHeading = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 18,
});

const NavLink = styled(Typography)({
  fontSize: "0.93rem",
  color: "rgba(15, 23, 42, 0.72)",
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-block",
  transition: "color 0.2s ease",
  "&:hover": { color: "#2f81ad" },
});

const ContactRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  marginBottom: 14,
  fontSize: "0.9rem",
  lineHeight: 1.5,
  color: "rgba(15, 23, 42, 0.72)",
  "& svg": {
    color: "#2f81ad",
    fontSize: 18,
    flexShrink: 0,
    marginTop: 2,
  },
});

const SocialButton = styled("a")({
  width: 36,
  height: 36,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  border: "1px solid rgba(47, 129, 173, 0.15)",
  textDecoration: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(47, 129, 173, 0.16)",
    borderColor: "rgba(47, 129, 173, 0.35)",
  },
  "& img": { width: 16, height: 16 },
});

const SubscribeButton = styled(Button)({
  paddingTop: 11,
  paddingBottom: 11,
  borderRadius: 10,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "0.95rem",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1d648c",
    boxShadow: "0 6px 14px rgba(47, 129, 173, 0.28)",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(47, 129, 173, 0.4)",
    color: "#fff",
  },
});

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/subscribe-newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to subscribe");
      }

      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 5000);
    } catch (err) {
      console.error("Subscription error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to subscribe. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "#fafbfc",
        color: "#0f172a",
        borderTop: "1px solid rgba(15, 23, 42, 0.06)",
        pt: { xs: 8, md: 11 },
        pb: { xs: 5, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 5 }} justifyContent="space-between">
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ mb: 3 }}>
              <img
                src={Logo}
                alt="Smart IT Innovation"
                style={{ maxWidth: 160, marginBottom: 14 }}
              />
              <Typography
                sx={{
                  fontSize: "0.93rem",
                  lineHeight: 1.65,
                  color: "rgba(15, 23, 42, 0.7)",
                  maxWidth: 320,
                  mb: 3,
                }}
              >
                A senior software and AI team partnering with startups and
                enterprise clients across North America since 2019.
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 3.5, flexWrap: "wrap" }}>
                {SOCIAL_LINKS.map((social) => (
                  <SocialButton
                    key={social.name}
                    href={social.href}
                    aria-label={social.name}
                  >
                    <img src={social.icon} alt="" />
                  </SocialButton>
                ))}
              </Box>

              <ContactRow>
                <LocationOnIcon />
                <span>Wilson Ave, Timmins, ON P4N 2S6, Canada</span>
              </ContactRow>
              <ContactRow>
                <LocationOnIcon />
                <span>
                  94-D Faisal Garden, Johar Town, Lahore, Punjab 50100, Pakistan
                </span>
              </ContactRow>
              <ContactRow>
                <PhoneIcon />
                <a
                  href="tel:+923407101829"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  +92 (340) 710-1829
                </a>
              </ContactRow>
              <ContactRow>
                <EmailIcon />
                <a
                  href="mailto:info@smartitinnovation.com"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  info@smartitinnovation.com
                </a>
              </ContactRow>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <ColumnHeading>Company</ColumnHeading>
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                p: 0,
                m: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
              }}
            >
              {NAV_LINKS.map((item) => (
                <li key={item.label}>
                  <NavLink onClick={() => navigate(item.route)}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <ColumnHeading>Services</ColumnHeading>
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                p: 0,
                m: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
              }}
            >
              {SERVICE_LINKS.map((item) => (
                <li key={item.label}>
                  <NavLink onClick={() => navigate(item.route)}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ColumnHeading>Newsletter</ColumnHeading>
            <Typography
              sx={{
                fontSize: "0.92rem",
                lineHeight: 1.6,
                color: "rgba(15, 23, 42, 0.7)",
                mb: 2.5,
              }}
            >
              Occasional notes on what we're building and learning. No spam.
            </Typography>

            <TextField
              fullWidth
              placeholder="you@company.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              disabled={loading}
              size="small"
              InputProps={{
                sx: {
                  borderRadius: 2.5,
                  backgroundColor: "#fff",
                  fontSize: "0.92rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(15, 23, 42, 0.12)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(47, 129, 173, 0.5)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2f81ad",
                  },
                },
              }}
              sx={{ mb: 1.5 }}
            />

            <SubscribeButton
              variant="contained"
              fullWidth
              onClick={handleSubscribe}
              disabled={loading}
              endIcon={<ArrowForwardIcon />}
              disableElevation
            >
              {loading ? "Subscribing…" : subscribed ? "Thank you!" : "Subscribe"}
            </SubscribeButton>
          </Grid>
        </Grid>

        <Divider
          sx={{ my: { xs: 5, md: 6 }, borderColor: "rgba(15, 23, 42, 0.08)" }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "0.85rem", color: "rgba(15, 23, 42, 0.55)" }}
          >
            © {new Date().getFullYear()} Smart IT Innovation. All rights reserved.
          </Typography>

          <Box sx={{ display: "flex", gap: 3 }}>
            {["Privacy Policy", "Terms of Service", "Cookies"].map((item) => (
              <Typography
                key={item}
                sx={{
                  fontSize: "0.85rem",
                  color: "rgba(15, 23, 42, 0.55)",
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                  "&:hover": { color: "#2f81ad" },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={subscribed}
        autoHideDuration={5000}
        onClose={() => setSubscribed(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Successfully subscribed to the newsletter!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer;
